"use client";

import React, { useState, useEffect, useRef } from "react";
import { BlurFade } from "@/components/ui/blur-fade";

// Animated progress bar that animates from 0 to target when in view
function AnimatedProgressBar({ 
  percent, 
  bgClass, 
  barClass 
}: { 
  percent: number; 
  bgClass: string; 
  barClass: string;
}) {
  const [width, setWidth] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          // Small delay to ensure the element is visible before animating
          setTimeout(() => setWidth(percent), 100);
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [percent, hasAnimated]);

  return (
    <div ref={ref} className={`w-full h-3 ${bgClass} rounded-full mb-4 overflow-hidden`}>
      <div
        className={`h-full ${barClass} rounded-full transition-all duration-1000 ease-out`}
        style={{ width: `${width}%` }}
      />
    </div>
  );
}
import Image from "next/image";
import Flag from "react-world-flags";

// Image with skeleton placeholder
function LoadingImage({
  src,
  alt,
  className,
  aspectRatio = "4/3",
}: {
  src: string;
  alt: string;
  className?: string;
  aspectRatio?: string;
}) {
  const [isLoaded, setIsLoaded] = useState(false);

  return (
    <div className="relative w-full overflow-hidden" style={{ aspectRatio }}>
      {!isLoaded && (
        <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 animate-pulse rounded-inherit" />
      )}
      <Image
        src={src}
        alt={alt}
        fill
        className={`${className} object-cover transition-opacity duration-500 rounded-inherit ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
        onLoad={() => setIsLoaded(true)}
        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
      />
    </div>
  );
}
import {
  Shield,
  Globe,
  Lock,
  Zap,
  Server,
  Eye,
  EyeOff,
  Wifi,
  Star,
  CheckCircle,
  Info,
  AlertCircle,
  MessageSquare,
  Key,
  Monitor,
  Smartphone,
  Laptop,
  ChevronRight,
  Plus,
} from "lucide-react";

const ICON_MAP: Record<string, React.ComponentType<any>> = {
  Shield,
  shield: Shield,
  Globe,
  globe: Globe,
  Lock,
  lock: Lock,
  Zap,
  zap: Zap,
  Server,
  server: Server,
  Eye,
  eye: Eye,
  EyeOff,
  "eye-off": EyeOff,
  Wifi,
  wifi: Wifi,
  Star,
  star: Star,
  CheckCircle,
  "check-circle": CheckCircle,
  Info,
  info: Info,
  AlertCircle,
  "alert-circle": AlertCircle,
  MessageSquare,
  "message-square": MessageSquare,
  Key,
  key: Key,
  Monitor,
  monitor: Monitor,
  Smartphone,
  smartphone: Smartphone,
  Laptop,
  laptop: Laptop,
  ChevronRight,
  "chevron-right": ChevronRight,
  Plus,
  plus: Plus,
};

// Flag codes use ISO 3166-1 alpha-2 (e.g., US, GB, FR, DE, CA, AU, JP, NL, ES, IT)

interface Card {
  // Card type: determines the rendering style
  type?: "image" | "content" | "flags" | "icons" | "stats" | "progress";

  // Image properties
  image?: string;
  imageAlt?: string;
  aspectRatio?: string;

  // Content properties
  badge?: {
    text: string;
    icon?: string;
    color?: string;
  };
  title?: string;
  description?: string;
  link?: {
    text: string;
    href: string;
  };

  // Flags (country codes)
  flags?: string[];

  // Platform icons (icon names)
  icons?: string[];

  // Flags caption
  flagsCaption?: string;

  // Stats card properties
  statNumber?: string;
  statLabel?: string;

  // Progress card properties
  progressValue?: number;
  progressLabel?: string;

  // Styling
  backgroundColor?: string;
  textColor?: string;

  // Optional link for entire card
  cardLink?: string;
}

interface Column {
  cards: Card[];
}

interface MasonryGridProps {
  title?: string;
  subtitle?: string;
  columns: Column[];
  theme?: "light" | "dark";
}

const MasonryGrid = ({ title, subtitle, columns, theme }: MasonryGridProps) => {
  // Default data if no columns provided
  const defaultColumns: Column[] = [
    {
      cards: [
        {
          type: "image",
          image: "/images/masonry-person.jpg",
          imageAlt: "VPN user",
          backgroundColor: "bg-gray-100",
        },
        {
          type: "icons",
          icons: ["Monitor", "smartphone", "laptop"],
          backgroundColor: "bg-white",
        },
      ],
    },
    {
      cards: [
        {
          type: "content",
          badge: { text: "VPN", icon: "wifi", color: "text-blue-600" },
          title: "The fastest VPN service",
          description:
            "Unmatched speed and post-quantum encryption, your new security standard for 2025.",
          link: { text: "What makes NordVPN so fast?", href: "#" },
          backgroundColor: "bg-white",
        },
        {
          type: "flags",
          flags: ["FR", "DE", "US", "GB", "CA"],
          description:
            "Choose from thousands of VPN servers covering 179+ locations.",
          backgroundColor: "bg-white",
        },
        {
          type: "content",
          badge: {
            text: "Dark Web Monitor",
            icon: "shield",
            color: "text-blue-600",
          },
          title: "Safer accounts and peace of mind",
          description:
            "Get instant alerts about leaked data and minimize risk.",
          backgroundColor: "bg-white",
        },
      ],
    },
    {
      cards: [
        {
          type: "content",
          title: "Global",
          description: "network of servers",
          backgroundColor: "bg-gray-100",
        },
        {
          type: "content",
          badge: { text: "Password management", icon: "key", color: "text-blue-600" },
          title: "Never type a password again",
          description:
            "Ditch passwords for passkeys — an easier way to log in on all your devices.",
          backgroundColor: "bg-white",
        },
      ],
    },
    {
      cards: [
        {
          type: "content",
          badge: {
            text: "Threat Protection Pro™",
            icon: "shield",
            color: "text-blue-600",
          },
          title: "Invisible protection from malware",
          description:
            "No need to think about it — automatically block phishing, malware, trackers, and ads.",
          backgroundColor: "bg-white",
        },
        {
          type: "image",
          image: "/images/masonry-security.jpg",
          imageAlt: "Security dashboard",
          backgroundColor: "bg-gradient-to-br from-blue-600 to-purple-600",
        },
      ],
    },
  ];

  const displayColumns = !columns || columns.length === 0 ? defaultColumns : columns;
  const displayTitle = title;
  const displaySubtitle = subtitle;

  // Tailwind dark: classes for theming
  const styles = {
    section: "bg-white dark:bg-zinc-900",
    title: "text-foreground dark:text-white",
    subtitle: "text-foreground/60 dark:text-white/60",
    cardBg: "bg-white dark:bg-zinc-800",
    cardBorder: "border-gray-200 hover:border-gray-300 dark:border-zinc-700 dark:hover:border-zinc-600",
    cardText: "text-foreground dark:text-white",
    cardTextMuted: "text-foreground/70 dark:text-white/70",
    iconColor: "text-gray-400 dark:text-zinc-400",
    progressBg: "bg-gray-200 dark:bg-zinc-600",
    progressBar: "bg-primary dark:bg-white",
  };

  const gridCols = {
    1: "grid-cols-1",
    2: "grid-cols-1 md:grid-cols-2",
    3: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
    4: "grid-cols-1 md:grid-cols-2 lg:grid-cols-4",
    5: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5",
    6: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6",
  }[displayColumns.length] || "grid-cols-1 md:grid-cols-2 lg:grid-cols-4";

  const renderCard = (card: Card, columnIndex: number, cardIndex: number) => {
    const BadgeIcon = card.badge?.icon ? (ICON_MAP[card.badge.icon] || Shield) : null;

    // Pure image card
    if (card.type === "image" || (card.image && !card.title && !card.description)) {
      const cardClasses = `block rounded-[2.5rem] lg:rounded-[3rem] overflow-hidden ${card.backgroundColor || styles.cardBg}`;

      return (
        <BlurFade
          key={cardIndex}
          delay={0.04 + cardIndex * 0.08}
          inView={true}
        >
          {card.cardLink ? (
            <a
              href={card.cardLink}
              className={`${cardClasses} hover:scale-[1.02] transition-transform duration-200`}
            >
              <LoadingImage
                src={card.image!}
                alt={card.imageAlt || "Masonry image"}
                aspectRatio={card.aspectRatio || "4/3"}
              />
            </a>
          ) : (
            <div className={cardClasses}>
              <LoadingImage
                src={card.image!}
                alt={card.imageAlt || "Masonry image"}
                aspectRatio={card.aspectRatio || "4/3"}
              />
            </div>
          )}
        </BlurFade>
      );
    }

    // Platform icons card
    if (card.type === "icons" && card.icons) {
      return (
        <BlurFade
          key={cardIndex}
          delay={0.04 + cardIndex * 0.08}
          inView={true}
        >
          <div
            className={`rounded-[3rem] lg:rounded-[4rem] p-[3rem] lg:p-[4rem] ${
              card.backgroundColor || styles.cardBg
            } border ${styles.cardBorder}`}
          >
            <div className="flex items-center justify-center gap-6 lg:p-[4rem]">
              {card.icons.map((iconName, i) => {
                const IconComponent = ICON_MAP[iconName] || Monitor;
                return (
                  <IconComponent
                    key={i}
                    className={`w-12 h-12 ${styles.iconColor}`}
                  />
                );
              })}
            </div>
          </div>
        </BlurFade>
      );
    }

    // Stats card - big number with label
    if (card.type === "stats") {
      return (
        <BlurFade
          key={cardIndex}
          delay={0.04 + cardIndex * 0.08}
          inView={true}
        >
          <div
            className={`rounded-[3rem] lg:rounded-[4rem] p-[3rem] lg:p-[4rem] ${
              card.backgroundColor || styles.cardBg
            } border ${styles.cardBorder}`}
          >
            {card.statNumber && (
              <p className={`text-4xl md:text-5xl lg:text-6xl font-bold ${styles.cardText} mb-2`}>
                {card.statNumber}
              </p>
            )}
            {card.statLabel && (
              <p className={`text-sm ${styles.cardTextMuted} leading-relaxed`}>
                {card.statLabel}
              </p>
            )}
          </div>
        </BlurFade>
      );
    }

    // Progress card - percentage with animated bar
    if (card.type === "progress") {
      const progressPercent = Math.min(100, Math.max(0, card.progressValue || 0));
      return (
        <BlurFade
          key={cardIndex}
          delay={0.04 + cardIndex * 0.08}
          inView={true}
        >
          <div
            className={`rounded-[3rem] lg:rounded-[4rem] p-[3rem] lg:p-[4rem] ${
              card.backgroundColor || styles.cardBg
            } border ${styles.cardBorder}`}
          >
            {/* Animated Progress bar */}
            <AnimatedProgressBar 
              percent={progressPercent} 
              bgClass={styles.progressBg} 
              barClass={styles.progressBar} 
            />
            {card.statNumber && (
              <p className={`text-3xl md:text-4xl font-bold ${styles.cardText} mb-2`}>
                {card.statNumber}
              </p>
            )}
            {card.progressLabel && (
              <p className={`text-sm ${styles.cardTextMuted} leading-relaxed`}>
                {card.progressLabel}
              </p>
            )}
          </div>
        </BlurFade>
      );
    }

    // Flags card
    if (card.type === "flags" && card.flags) {
      // Calculate scale factors for each flag position
      const total = card.flags.length;
      const center = (total - 1) / 2;
      // Base delay: card animation delay + time for card to animate in
      const cardAnimDelay = 0.04 + cardIndex * 0.08;
      const cardAnimDuration = 0.5; // BlurFade duration
      const baseDelay = cardAnimDelay + cardAnimDuration;
      
      const scales = card.flags.map((_, i) => {
        const dist = Math.abs(i - center);
        return Math.max(0.6, 1 - dist * 0.2);
      });
      
      return (
        <BlurFade
          key={cardIndex}
          delay={0.04 + cardIndex * 0.08}
          inView={true}
        >
          <div
            className={`rounded-[2rem] lg:rounded-[3rem] py-4 px-2 sm:p-6 lg:p-8 ${
              card.backgroundColor || styles.cardBg
            } border ${styles.cardBorder}`}
          >
            <div 
              className="grid mb-3 mx-auto place-items-center gap-2"
              style={{
                width: '80%',
                gridTemplateColumns: scales.map(s => `${s}fr`).join(' ')
              }}
            >
              {card.flags.map((code, i) => {
                const flagUrl = `https://flagcdn.com/w160/${code.toLowerCase()}.png`;
                const distFromCenter = Math.abs(i - center);
                const animationDelay = baseDelay + (distFromCenter * 0.1); // base + 100ms stagger from center
                return (
                  <div 
                    key={i} 
                    className="flag-pop-in rounded-full bg-cover bg-center aspect-square w-full"
                    style={{
                      backgroundImage: `url(${flagUrl})`,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      animationDelay: `${animationDelay}s`,
                    }}
                  />
                );
              })}
            </div>
            {card.flagsCaption && (
              <p className={`text-xs text-center ${styles.cardTextMuted} leading-relaxed`}>
                {card.flagsCaption}
              </p>
            )}
          </div>
        </BlurFade>
      );
    }

    // Content card (default)
    return (
      <BlurFade
        key={cardIndex}
        delay={0.04 + cardIndex * 0.08}
          inView={true}
      >
        <div
          className={`rounded-[3rem] lg:rounded-[4rem] p-[3rem] lg:p-[4rem] ${
            card.backgroundColor || styles.cardBg
          } ${
            card.textColor || styles.cardText
          } border ${styles.cardBorder} transition-all duration-200`}
        >
          {/* Badge */}
          {card.badge && (
            <div
              className={`flex items-center gap-2 ${
                card.badge.color || "text-blue-600"
              } text-sm font-medium mb-3`}
            >
              {BadgeIcon && <BadgeIcon className="w-4 h-4" />}
              <span>{card.badge.text}</span>
            </div>
          )}

          {/* Title */}
          {card.title && (
            <h3
              className={`text-xl font-semibold mb-2 ${
                card.textColor || styles.cardText
              }`}
            >
              {card.title}
            </h3>
          )}

          {/* Description */}
          {card.description && (
            <p
              className={`text-sm leading-relaxed ${
                card.textColor
                  ? `${card.textColor}/80`
                  : styles.cardTextMuted
              }`}
            >
              {card.description}
            </p>
          )}

          {/* Link */}
          {card.link && (
            <a
              href={card.link.href}
              className="inline-flex items-center gap-1 text-blue-600 font-medium text-sm mt-3 hover:gap-2 transition-all"
            >
              {card.link.text}
              <Plus className="w-4 h-4" />
            </a>
          )}

          {/* Image (if included in content card) */}
          {card.image && (card.title || card.description) && (
            <div className="relative w-full h-48 mt-4 rounded-lg overflow-hidden">
              <Image
                src={card.image}
                alt={card.imageAlt || card.title || "Card image"}
                fill
                className="object-cover"
              />
            </div>
          )}
        </div>
      </BlurFade>
    );
  };

  // When theme="dark" is set, force dark mode within this component
  // Site dark mode will also trigger dark: classes via Tailwind
  const forceDark = theme === "dark";
  
  return (
    <div className={forceDark ? "dark" : ""}>
    <section className={`py-16 md:py-24 px-4 md:px-6 ${styles.section}`}>
      <div className="container max-w-7xl mx-auto">
        {/* Header */}
        {(displayTitle || displaySubtitle) && (
          <div className="mb-12">
            {displayTitle && (
              <BlurFade delay={0.1}>
                <h2 className={`text-3xl md:text-5xl font-bold ${styles.title} mb-3`}>
                  {displayTitle}
                </h2>
              </BlurFade>
            )}
            {displaySubtitle && (
              <BlurFade delay={0.15}>
                <p className={`text-lg ${styles.subtitle} max-w-2xl`}>
                  {displaySubtitle}
                </p>
              </BlurFade>
            )}
          </div>
        )}

        {/* Masonry Grid */}
        <div className={`grid ${gridCols} gap-4`}>
          {displayColumns.map((column, columnIndex) => (
            <div key={columnIndex} className="flex flex-col gap-4">
              {column.cards.map((card, cardIndex) =>
                renderCard(card, columnIndex, cardIndex)
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
    </div>
  );
};

export default MasonryGrid;
